import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useUpdateInvoiceAdditionalDetails } from '../../../../../../hooks/requests/use-update-invoice-additional-details';
import { queryKeys } from '../../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../../utils/vy-mutation';

type UseUpdateInvoiceNumberParams = {
  invoiceId: string;
  currentInvoiceNumber: string | null;
  currentRef: string;
};

export const useUpdateInvoiceNumber = ({
  invoiceId,
  currentInvoiceNumber,
  currentRef,
}: UseUpdateInvoiceNumberParams) => {
  const queryClient = useQueryClient();
  const updateInvoiceAdditionalDetailsMutation = useUpdateInvoiceAdditionalDetails();

  const handleInvoiceNumberChange = useCallback(
    async (event: React.FocusEvent<HTMLInputElement>) => {
      const trimmedValue = event.target.value.trim();
      const currentValue = currentInvoiceNumber ?? currentRef;

      if (trimmedValue === currentValue) {
        return;
      }

      await runMutation(
        updateInvoiceAdditionalDetailsMutation,
        {
          id: invoiceId,
          invoiceNumber: trimmedValue === '' ? undefined : trimmedValue,
        },
        {
          pending: 'Updating invoice number...',
          success: 'Invoice number updated',
          error: 'Failed to update invoice number',
        },
        {
          onSuccess: async () => {
            await Promise.all([
              queryClient.invalidateQueries(queryKeys.dynamicInvoice(invoiceId)),
              queryClient.invalidateQueries(queryKeys.invoicesOverviewsNew()),
              queryClient.invalidateQueries(queryKeys.invoices(invoiceId)),
            ]);
          },
        },
      );
    },
    [currentInvoiceNumber, currentRef, invoiceId, queryClient, updateInvoiceAdditionalDetailsMutation],
  );

  return handleInvoiceNumberChange;
};
